import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { CalendarCheck, Save, Search, CheckCircle, XCircle, Clock, History, CalendarDays } from 'lucide-react';

const Asistencias = () => {
  const navigate = useNavigate();
  const [periodos, setPeriodos] = useState([]);
  const [aulas, setAulas] = useState([]);
  const [alumnos, setAlumnos] = useState([]);

  const [vistaActual, setVistaActual] = useState('registrar'); 

  const [filtros, setFiltros] = useState({ periodoId: '', aulaId: '', fecha: new Date().toISOString().split('T')[0] });
  
  const [asistencias, setAsistencias] = useState({});
  const [guardando, setGuardando] = useState(false);

  const [fechaHistorial, setFechaHistorial] = useState(new Date().toISOString().split('T')[0]);
  const [historialDatos, setHistorialDatos] = useState([]);

  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().toISOString().slice(0, 7)); 
  const [reporteMensual, setReporteMensual] = useState([]);
  const [diasDelMes, setDiasDelMes] = useState([]);

  useEffect(() => { cargarPeriodos(); }, []);
  useEffect(() => { if (filtros.periodoId) cargarAulas(filtros.periodoId); }, [filtros.periodoId]);
  
  useEffect(() => { 
    if (filtros.aulaId && vistaActual === 'registrar') {
      cargarAlumnos(); 
      verificarAsistenciaExistente(filtros.fecha);
    }
  }, [filtros.aulaId, filtros.fecha, vistaActual]);

  const cargarPeriodos = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_URL}/Periodos`, { headers: { 'Authorization': `Bearer ${token}` } });
    if (res.ok) setPeriodos(await res.json());
  };

  const cargarAulas = async (periodoId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_URL}/Aulas/PorPeriodo/${periodoId}`, { headers: { 'Authorization': `Bearer ${token}` } });
    if (res.ok) setAulas(await res.json());
  };

  const cargarAlumnos = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_URL}/Alumnos/PorAula/${filtros.periodoId}/${filtros.aulaId}`, { headers: { 'Authorization': `Bearer ${token}` } });
    if (res.ok) {
      const data = await res.json();
      setAlumnos(data);
      const defaultAsist = {};
      data.forEach(a => defaultAsist[a.id || a.Id] = 'Presente');
      setAsistencias(defaultAsist);
    }
  };

  // ==========================================
  // TRADUCTOR UNIVERSAL DE FECHAS (NUEVO)
  // ==========================================
  const normalizarFecha = (fechaDb) => {
    if (!fechaDb) return "";
    const str = String(fechaDb);
    if (str.includes('T')) return str.split('T')[0]; // Corta las horas de C#
    return str.substring(0, 10); // Asegura que solo tengamos YYYY-MM-DD
  };

const verificarAsistenciaExistente = async (fechaBuscada) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Asistencias/PorAulaYFecha/${filtros.aulaId}/${fechaBuscada}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const filtradas = await res.json();
        if (filtradas.length > 0) {
          const dictExistente = {};
          filtradas.forEach(a => { dictExistente[a.alumnoId || a.AlumnoId] = a.estado || a.Estado; });
          setAsistencias(dictExistente);
        }
      }
    } catch (e) { console.error("Error al verificar asistencia:", e); }
  };

  const handleCambiarEstado = (alumnoId, estado) => { setAsistencias(prev => ({ ...prev, [alumnoId]: estado })); };

 const handleGuardarAsistencia = async () => {
    setGuardando(true);
    const token = localStorage.getItem('token');
    
    // Armamos la lista completa para mandarla de un solo golpe a GuardarMasivo
    const payloadMasivo = alumnos.map(alumno => {
      const idReal = alumno.id || alumno.Id;
      return {
        alumnoId: idReal, 
        aulaId: parseInt(filtros.aulaId),
        fecha: filtros.fecha, 
        estado: asistencias[idReal] || 'Presente',
        observaciones: "", 
        activo: true
      };
    });

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Asistencias/GuardarMasivo`, {
        method: 'POST', 
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payloadMasivo)
      });
      
      if (res.ok) alert('¡Asistencia masiva registrada correctamente!');
      else alert('Hubo un error en el servidor al guardar.');
    } catch (e) { alert('Error de conexión al guardar.'); } 
    finally { setGuardando(false); }
  };

  // ==========================================
  // HISTORIAL DIARIO (CORREGIDO Y BLINDADO)
  // ==========================================
const handleBuscarHistorial = async () => {
    if (!filtros.aulaId) return alert("Selecciona un Aula en la parte superior primero.");
    const token = localStorage.getItem('token');
    try {
      const [resAlumnos, resAsistencias] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/Alumnos/PorAula/${filtros.periodoId}/${filtros.aulaId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_URL}/Asistencias/PorAulaYFecha/${filtros.aulaId}/${fechaHistorial}`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (resAlumnos.ok && resAsistencias.ok) {
        const alumnosData = await resAlumnos.json();
        const asistenciasData = await resAsistencias.json();
        
        const historialCruzado = alumnosData.map(al => {
          const alId = al.id || al.Id;
          const registro = asistenciasData.find(as => (as.alumnoId || as.AlumnoId) === alId);
          return { 
            id: alId, 
            nombreCompleto: `${al.apellidos || al.Apellidos}, ${al.nombres || al.Nombres}`, 
            estado: registro ? (registro.estado || registro.Estado) : 'No registrado' 
          };
        });
        setHistorialDatos(historialCruzado);
      }
    } catch (e) { alert("Error de conexión al cargar historial."); }
  };

  // ==========================================
  // REPORTE MENSUAL EN BLOQUE (CORREGIDO)
  // ==========================================
const handleGenerarReporteMensual = async () => {
    if (!filtros.aulaId || !mesSeleccionado) return alert("Selecciona un Aula y un Mes.");
    const token = localStorage.getItem('token');
    
    const [año, mes] = mesSeleccionado.split('-');
    const totalDias = new Date(año, mes, 0).getDate();
    const arrayDias = Array.from({length: totalDias}, (_, i) => i + 1);
    setDiasDelMes(arrayDias);

    try {
      const [resAlumnos, resAsistencias] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/Alumnos/PorAula/${filtros.periodoId}/${filtros.aulaId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        // LLAMAMOS A LA NUEVA RUTA DE C# QUE TRAE TODA EL AULA
        fetch(`${process.env.REACT_APP_API_URL}/Asistencias/PorAula/${filtros.aulaId}`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (resAlumnos.ok && resAsistencias.ok) {
        const alumnosData = await resAlumnos.json();
        const asistenciasData = await resAsistencias.json();

        const asistenciasDelMes = asistenciasData.filter(a => {
          const aFechaLimpia = normalizarFecha(a.fecha || a.Fecha);
          return aFechaLimpia.startsWith(mesSeleccionado);
        });

        const matriz = alumnosData.map(al => {
          const alId = al.id || al.Id;
          let faltasTotales = 0; let tardanzasTotales = 0; let diasAsistidos = {};

          arrayDias.forEach(dia => {
            const diaStr = dia.toString().padStart(2, '0');
            const fechaExacta = `${mesSeleccionado}-${diaStr}`;
            const registroDia = asistenciasDelMes.find(a => (a.alumnoId || a.AlumnoId) === alId && normalizarFecha(a.fecha || a.Fecha) === fechaExacta);
            
            if (registroDia) {
              const est = registroDia.estado || registroDia.Estado;
              if (est === 'Presente') diasAsistidos[dia] = 'P';
              else if (est === 'Falta') { diasAsistidos[dia] = 'F'; faltasTotales++; }
              else if (est === 'Tardanza') { diasAsistidos[dia] = 'T'; tardanzasTotales++; }
            } else { diasAsistidos[dia] = '-'; }
          });
          return { id: alId, nombreCompleto: `${al.apellidos || al.Apellidos}, ${al.nombres || al.Nombres}`, dias: diasAsistidos, faltasTotales, tardanzasTotales };
        });
        setReporteMensual(matriz);
      }
    } catch (e) { alert("Error al cargar reporte mensual."); }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />
      <main className="flex-1 p-6 overflow-hidden flex flex-col">
        <header className="mb-4">
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2"><CalendarCheck className="text-blue-600" /> Control de Asistencia</h1>
        </header>

        {/* CONTROLES SUPERIORES */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 mb-4 flex-shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs font-bold text-slate-500 mb-1">Año Escolar</label>
              <select value={filtros.periodoId} onChange={e => setFiltros({...filtros, periodoId: e.target.value})} className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm outline-none">
                <option value="">Seleccionar</option>{periodos.map(p => <option key={p.id || p.Id} value={p.id || p.Id}>{p.nombre || p.Nombre}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-bold text-slate-500 mb-1">Aula / Salón</label>
              <select value={filtros.aulaId} onChange={e => setFiltros({...filtros, aulaId: e.target.value})} disabled={!filtros.periodoId} className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm disabled:opacity-50 outline-none">
                <option value="">Seleccionar</option>{aulas.map(a => <option key={a.id || a.Id} value={a.id || a.Id}>{a.grado || a.Grado} "{a.seccion || a.Seccion}"</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* PESTAÑAS (3 VISTAS) */}
        <div className="flex bg-white rounded-t-xl border-b text-sm font-medium text-slate-500 flex-shrink-0">
          <button onClick={() => setVistaActual('registrar')} className={`flex-1 p-3 flex justify-center items-center gap-2 ${vistaActual === 'registrar' ? 'bg-white text-blue-600 border-b-2 border-blue-600 font-bold' : 'hover:bg-slate-50'}`}><CalendarCheck size={18}/> Tomar Lista (Hoy)</button>
          <button onClick={() => { setVistaActual('diario'); setHistorialDatos([]); }} className={`flex-1 p-3 flex justify-center items-center gap-2 ${vistaActual === 'diario' ? 'bg-white text-blue-600 border-b-2 border-blue-600 font-bold' : 'hover:bg-slate-50'}`}><History size={18}/> Buscar un Día</button>
          <button onClick={() => { setVistaActual('mensual'); setReporteMensual([]); }} className={`flex-1 p-3 flex justify-center items-center gap-2 ${vistaActual === 'mensual' ? 'bg-white text-blue-600 border-b-2 border-blue-600 font-bold' : 'hover:bg-slate-50'}`}><CalendarDays size={18}/> Reporte Mensual</button>
        </div>

        {/* CONTENEDOR PRINCIPAL */}
        <div className="bg-white p-6 rounded-b-xl shadow-sm border border-t-0 border-slate-100 flex-1 overflow-auto">
          
          {/* VISTA 1: REGISTRAR */}
          {vistaActual === 'registrar' && (
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-between items-center mb-6 bg-slate-50 p-3 rounded border">
                <div className="flex items-center gap-3">
                  <label className="font-bold text-slate-700">Fecha de Registro:</label>
                  <input type="date" value={filtros.fecha} onChange={e => setFiltros({...filtros, fecha: e.target.value})} className="p-2 border rounded outline-none font-medium text-blue-600" />
                </div>
                <button onClick={handleGuardarAsistencia} disabled={guardando || alumnos.length === 0} className="bg-blue-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 disabled:bg-slate-400">
                  <Save size={18} /> {guardando ? 'Guardando...' : 'Guardar Asistencia'}
                </button>
              </div>

              {alumnos.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-slate-100 border-b"><tr className="text-sm text-slate-600"><th className="p-4 font-bold">Estudiante</th><th className="p-4 font-bold text-center">Estado</th></tr></thead>
                    <tbody>
                      {alumnos.map(alumno => {
                        const aId = alumno.id || alumno.Id;
                        return (
                          <tr key={aId} className="border-b hover:bg-slate-50">
                            <td className="p-4 font-medium text-slate-800">{alumno.apellidos || alumno.Apellidos}, {alumno.nombres || alumno.Nombres}</td>
                            <td className="p-4 flex justify-center gap-2">
                              <button onClick={() => handleCambiarEstado(aId, 'Presente')} className={`px-4 py-2 rounded-lg font-bold text-xs border flex items-center gap-1 ${asistencias[aId] === 'Presente' ? 'bg-green-100 text-green-700 border-green-300 shadow-inner' : 'bg-white text-slate-400 hover:bg-slate-50'}`}><CheckCircle size={14}/> Presente</button>
                              <button onClick={() => handleCambiarEstado(aId, 'Falta')} className={`px-4 py-2 rounded-lg font-bold text-xs border flex items-center gap-1 ${asistencias[aId] === 'Falta' ? 'bg-red-100 text-red-700 border-red-300 shadow-inner' : 'bg-white text-slate-400 hover:bg-slate-50'}`}><XCircle size={14}/> Falta</button>
                              <button onClick={() => handleCambiarEstado(aId, 'Tardanza')} className={`px-4 py-2 rounded-lg font-bold text-xs border flex items-center gap-1 ${asistencias[aId] === 'Tardanza' ? 'bg-amber-100 text-amber-700 border-amber-300 shadow-inner' : 'bg-white text-slate-400 hover:bg-slate-50'}`}><Clock size={14}/> Tardanza</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : ( <p className="text-center text-slate-400 py-8">Selecciona un aula para mostrar la lista.</p> )}
            </div>
          )}

          {/* VISTA 2: HISTORIAL DIARIO */}
          {vistaActual === 'diario' && (
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-between items-center mb-6 bg-slate-50 p-3 rounded border">
                <div className="flex items-center gap-3">
                  <label className="font-bold text-slate-700">Buscar Fecha:</label>
                  <input type="date" value={fechaHistorial} onChange={e => setFechaHistorial(e.target.value)} className="p-2 border rounded outline-none font-medium text-blue-600" />
                </div>
                <button onClick={handleBuscarHistorial} disabled={!filtros.aulaId} className="bg-slate-800 text-white px-5 py-2 rounded-lg font-medium hover:bg-slate-900 flex items-center gap-2 disabled:bg-slate-400">
                  <Search size={18} /> Buscar Registros
                </button>
              </div>

              {historialDatos.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-slate-100 border-b"><tr className="text-sm text-slate-600"><th className="p-4 font-bold">Estudiante</th><th className="p-4 font-bold text-center">Estado Registrado</th></tr></thead>
                    <tbody>
                      {historialDatos.map(dato => (
                        <tr key={dato.id} className="border-b hover:bg-slate-50">
                          <td className="p-4 font-medium text-slate-800">{dato.nombreCompleto}</td>
                          <td className="p-4 text-center">
                            {dato.estado === 'Presente' && <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">Presente</span>}
                            {dato.estado === 'Falta' && <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold">Falta</span>}
                            {dato.estado === 'Tardanza' && <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-bold">Tardanza</span>}
                            {dato.estado === 'No registrado' && <span className="text-slate-400 text-xs italic">Sin registrar</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : ( <p className="text-center text-slate-400 py-8">Selecciona la fecha y haz clic en "Buscar Registros".</p> )}
            </div>
          )}

          {/* VISTA 3: REPORTE MENSUAL EN BLOQUE */}
          {vistaActual === 'mensual' && (
            <div>
              <div className="flex justify-between items-center mb-6 bg-slate-50 p-3 rounded border">
                <div className="flex items-center gap-3">
                  <label className="font-bold text-slate-700">Mes a Consultar:</label>
                  <input type="month" value={mesSeleccionado} onChange={e => setMesSeleccionado(e.target.value)} className="p-2 border rounded outline-none font-medium text-blue-600" />
                </div>
                <button onClick={handleGenerarReporteMensual} disabled={!filtros.aulaId} className="bg-green-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-green-700 flex items-center gap-2 disabled:bg-slate-400">
                  <CalendarDays size={18} /> Generar Cuadrícula
                </button>
              </div>

              {reporteMensual.length > 0 ? (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-left border-collapse whitespace-nowrap text-sm">
                    <thead className="bg-slate-100 border-b">
                      <tr>
                        <th className="p-3 font-bold sticky left-0 bg-slate-100 z-10 border-r min-w-[200px]">Estudiante</th>
                        {diasDelMes.map(dia => (
                          <th key={dia} className="p-2 font-bold text-center border-r min-w-[30px]">{dia}</th>
                        ))}
                        <th className="p-3 font-bold text-red-600 text-center bg-red-50 border-l">Tot. Faltas</th>
                        <th className="p-3 font-bold text-amber-600 text-center bg-amber-50">Tot. Tard.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reporteMensual.map(fila => (
                        <tr key={fila.id} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium sticky left-0 bg-white border-r shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">{fila.nombreCompleto}</td>
                          
                          {diasDelMes.map(dia => {
                            const estado = fila.dias[dia];
                            let colorClase = "text-slate-300";
                            if (estado === 'P') colorClase = "text-green-600 font-bold bg-green-50";
                            if (estado === 'F') colorClase = "text-red-600 font-black bg-red-50";
                            if (estado === 'T') colorClase = "text-amber-600 font-bold bg-amber-50";
                            
                            return (
                              <td key={dia} className={`p-2 text-center border-r ${colorClase}`}>
                                {estado}
                              </td>
                            );
                          })}

                          <td className="p-3 text-center font-bold text-red-600 bg-red-50 border-l">{fila.faltasTotales}</td>
                          <td className="p-3 text-center font-bold text-amber-600 bg-amber-50">{fila.tardanzasTotales}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : ( <p className="text-center text-slate-400 py-8">Haz clic en "Generar Cuadrícula" para ver la matriz mensual.</p> )}
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default Asistencias;